import type { ProvisioningSchedules } from '../types/modpackProvisioning';
import type { UpdaterRequest, UpdaterScheduleRecovery } from '../types/modpackUpdater';

export const provisioningCanAnswerRequest = (request: UpdaterRequest, canWrite: boolean, canApprove: boolean) =>
  request.kind === 'schedule' ? canApprove : canWrite;

export const scheduleSpacing = (seconds: number) => seconds % 60 === 0 ? `${seconds / 60} minutes` : `${seconds} seconds`;

/** Counts scheduled actions, including multiple actions in a single timed entry. */
export function provisioningScheduleCounts(schedules: ProvisioningSchedules, original = false) {
  return schedules.entries.reduce((counts, entry) => {
    if (entry.kind !== 'time') return counts;
    const occurrences = (original ? entry.original_times : entry.times)?.length ?? 0;
    counts.backup += occurrences * entry.actions.filter(action => action.category === 'backup').length;
    counts.restart += occurrences * entry.actions.filter(action => ['restart', 'start'].includes(action.category)).length;
    return counts;
  }, { backup: 0, restart: 0 });
}

const seconds = (value: unknown) => Number.isSafeInteger(value) && Number(value) >= 60 && Number(value) <= 3600;
const times = (value: unknown): value is number[] => Array.isArray(value) && value.length > 0
  && value.every(time => Number.isSafeInteger(time) && time >= 0 && time < 1440) && new Set(value).size === value.length;

const recoveryPolicy = (policy: ProvisioningSchedules['conflict_policy']) => policy?.backup_routine === 'allowed_and_disclosed'
  && (policy.version === 4 && policy.heavy === 'host_and_instance' && policy.routine === 'staggered_routine_and_own_lifecycle'
    || policy.version === 5 && policy.heavy === 'backup_and_restart' && policy.routine === 'not_exclusive');

/** Reject incomplete previews before presenting an option as available for confirmation. */
export function updaterScheduleRecovery(request: UpdaterRequest): UpdaterScheduleRecovery | null {
  const recovery = request.schedule;
  if (request.kind !== 'schedule' || !recovery || recovery.schema !== 1
    || recovery.original_policy?.backup_seconds !== 3600 || recovery.original_policy.restart_start_seconds !== 3600
    || !Array.isArray(recovery.options) || recovery.options.length > 12
    || new Set(recovery.options.map(option => option?.id)).size !== recovery.options.length) return null;
  for (const option of recovery.options) {
    const schedules = option?.schedules;
    const policy = schedules?.conflict_policy;
    if (!option || typeof option.id !== 'string' || !/^[a-f0-9]{64}$/.test(option.id)
      || typeof option.label !== 'string' || !option.label.trim()
      || !seconds(option.backup_seconds) || !seconds(option.restart_start_seconds)
      || !Number.isSafeInteger(option.backup_count) || option.backup_count < 1
      || !Number.isSafeInteger(option.restart_count) || option.restart_count < 0
      || !schedules || typeof schedules.timezone !== 'string' || schedules.resolution !== undefined
      || !policy || !('start_spacing' in policy) || !recoveryPolicy(policy) || policy.start_spacing?.backup_seconds !== option.backup_seconds
      || policy.start_spacing.restart_start_seconds !== option.restart_start_seconds
      || schedules.estimated_overlap_minutes !== 0 || !Array.isArray(schedules.entries)
      || !Array.isArray(schedules.warnings) || !schedules.warnings.every(warning => typeof warning === 'string')
      || !Array.isArray(schedules.skipped) || !schedules.skipped.every(skipped => skipped && typeof skipped.reason === 'string'
        && (skipped.actions === undefined || Array.isArray(skipped.actions) && skipped.actions.every(action => action && typeof action.reason === 'string')))) return null;
    for (const entry of schedules.entries) {
      if (!entry || !['time', 'event'].includes(entry.kind) || typeof entry.key !== 'string' || !Array.isArray(entry.actions)
        || !entry.actions.every(action => action && typeof action.method === 'string' && typeof action.category === 'string'
          && Number.isFinite(action.offset_seconds) && Number.isFinite(action.duration_minutes))
        || (entry.intervals_minutes !== undefined && (!Array.isArray(entry.intervals_minutes) || !entry.intervals_minutes.every(Number.isFinite)))) return null;
      if (entry.kind === 'time' && (!times(entry.times) || !times(entry.original_times)
        || entry.times.length > entry.original_times.length
        || entry.times.length !== entry.original_times.length && entry.cadence_changed !== true)) return null;
    }
    const actual = provisioningScheduleCounts(schedules);
    if (actual.backup !== option.backup_count || actual.restart !== option.restart_count) return null;
  }
  return recovery;
}
